// Ein Rosenkrantz-Beleg, gerendert aus der JSON der Schnittstelle — so, wie die
// Rechnung auf Papier aussähe. Betreiber 14.09.2026: „because they are generated
// from a json data, you may simulate something which looks like a Rosenkrantz
// invoice". Wer eine Kundennummer zuordnen soll, will den ganzen Beleg sehen.
//
// Die LIEFERadresse ist hervorgehoben: sie ist der einzige Hinweis auf die
// Schule. Die Rechnungsadresse ist immer die Zentrale. Der Name in der
// Lieferadresse ist nachweislich unzuverlässig — Straße und PLZ zählen.
//
// Umschalter »Beleg | Roh-JSON« (Betreiber 15.09.2026): zur Diagnose soll der
// ganze archivierte Umschlag einsehbar sein, nicht nur das gerenderte Beleg-
// Layout — inkl. meta/account_code/payload, unveraendert. Jeder bestehende
// Einstieg (Assistent, Rueckstand, Auswertung) bekommt die Rohdaten damit
// kostenlos mit.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge, Button, Segmented } from '../../../../shared/ui'
import type { RkBelegUmschlag, RkAdresse } from '../api/rosenkrantzApi'

const eur = (v: number | undefined | null) =>
  v == null ? '—' : v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
const num = (v: number | undefined | null, d = 3) =>
  v == null ? '—' : v.toLocaleString('de-DE', { maximumFractionDigits: d })
const dt = (iso: string | undefined | null) =>
  iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'

function Adresse({ a }: { a: RkAdresse | undefined }) {
  if (!a) return <p className="text-on-surface-variant">—</p>
  return (
    <address className="not-italic text-xs leading-relaxed">
      {a.Name && <div className="font-medium text-on-surface">{a.Name}</div>}
      {a.Street && <div>{a.Street}</div>}
      {a.Street2 && <div className="text-on-surface-variant">{a.Street2}</div>}
      <div>{[a.PostalCode, a.City].filter(Boolean).join(' ')}</div>
      {a.CountryCode && a.CountryCode !== 'DE' && <div>{a.CountryCode}</div>}
    </address>
  )
}

export default function RosenkrantzBelegAnsicht({ umschlag, hinweisAdresse }: {
  umschlag: RkBelegUmschlag
  /** Portal-Adressbuch: Bezeichnung + id der Lieferadresse, wenn bekannt. */
  hinweisAdresse?: { id: number | null; code: string | null } | null
}) {
  const { t } = useTranslation('kostenstellen')
  const [ansicht, setAnsicht] = useState<'beleg' | 'roh'>('beleg')
  const [kopiert, setKopiert] = useState(false)
  const p = umschlag.payload ?? {}
  const doc = p.Document ?? {}
  const lines = Array.isArray(p.Lines) ? p.Lines : []
  const istGutschrift = (p.Totals?.NetAmount ?? 0) < 0

  async function kopieren() {
    try {
      await navigator.clipboard.writeText(JSON.stringify(umschlag, null, 2))
      setKopiert(true)
      setTimeout(() => setKopiert(false), 1500)
    } catch {
      // Clipboard-API kann fehlen (kein sicherer Kontext, alter Browser) — dann
      // einfach keine Rueckmeldung, kein Absturz.
    }
  }

  return (
    <div className="rounded-xl border border-outline-variant bg-surface-container-lowest p-4 space-y-4 text-xs text-on-surface">
      <div className="flex justify-end">
        <Segmented<'beleg' | 'roh'>
          size="xs"
          value={ansicht}
          onChange={setAnsicht}
          options={[
            { value: 'beleg', label: t('imports.rosenkrantzBeleg.ansichtBeleg') },
            { value: 'roh', label: t('imports.rosenkrantzBeleg.ansichtRoh') },
          ]}
        />
      </div>

      {ansicht === 'roh' ? (
        <div className="space-y-2">
          <div className="flex justify-end">
            <Button size="sm" variant="ghost" icon="content_copy" onClick={kopieren}>
              {kopiert ? t('imports.rosenkrantzBeleg.kopiert') : t('imports.rosenkrantzBeleg.kopieren')}
            </Button>
          </div>
          <pre className="max-h-[60vh] overflow-auto rounded-lg bg-surface-container-low p-3 font-mono text-[11px] leading-relaxed whitespace-pre-wrap">
            {JSON.stringify(umschlag, null, 2)}
          </pre>
        </div>
      ) : (
      <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-semibold">
            {istGutschrift ? t('imports.rosenkrantzBeleg.gutschrift') : t('imports.rosenkrantzBeleg.rechnung')} {doc.Number ?? '—'}
            {doc.Type && doc.Type !== 'invoice' && <Badge tone="warn">{doc.Type}</Badge>}
          </p>
          <p className="text-on-surface-variant">
            {t('imports.rosenkrantzBeleg.datum')} {dt(doc.Date)}
            {doc.CustomerReference && <> · {t('imports.rosenkrantzBeleg.referenz')} {doc.CustomerReference}</>}
            {p.Payment?.DueDate && <> · {t('imports.rosenkrantzBeleg.faellig')} {dt(p.Payment.DueDate)}</>}
          </p>
        </div>
        <div className="text-right text-on-surface-variant">
          <div>{t('imports.rosenkrantzBeleg.kunde')} <span className="font-mono text-on-surface">{p.Customer?.Number ?? umschlag.account_code ?? '—'}</span></div>
          {p.Source?.System && <div>{p.Source.System}{p.Source.DocEntry != null && <> · #{p.Source.DocEntry}</>}</div>}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-outline-variant/60 p-3">
          <p className="text-[11px] uppercase tracking-wide text-on-surface-variant mb-1">{t('imports.rosenkrantzBeleg.rechnungsadresse')}</p>
          <Adresse a={p.BillingAddress} />
        </div>
        <div className="rounded-lg border-2 border-primary/50 bg-primary/5 p-3">
          <p className="text-[11px] uppercase tracking-wide text-on-surface-variant mb-1">
            {t('imports.rosenkrantzBeleg.lieferadresse')}{' '}
            <Badge tone="info">{t('imports.rosenkrantzBeleg.schulhinweis')}</Badge>
          </p>
          <Adresse a={p.ShippingAddress} />
          {hinweisAdresse?.code ? (
            <p className="mt-2 text-[11px] text-on-surface-variant">
              {t('imports.rosenkrantzBeleg.portalAdresse', { id: hinweisAdresse.id ?? '?', code: hinweisAdresse.code })}
            </p>
          ) : hinweisAdresse?.id != null ? (
            <p className="mt-2 text-[11px] text-on-surface-variant">
              {t('imports.rosenkrantzBeleg.portalAdresseIdOnly', { id: hinweisAdresse.id })}
            </p>
          ) : (p.ShippingAddress?.Street || p.ShippingAddress?.PostalCode) && (
            // Kein aufgeloester Adressbuch-Hinweis zur Hand (Task D/f,
            // rosenkrantz-routing #8) — die Adresse steht oben schon in der
            // Box, aber als eigene "Adresse:"-Zeile bleibt die Aussage
            // konsistent mit den Faellen, die einen Hinweis haben.
            <p className="mt-2 text-[11px] text-on-surface-variant">
              {t('imports.rosenkrantzBeleg.adresseFallback', {
                street: p.ShippingAddress?.Street ?? '—',
                plz: p.ShippingAddress?.PostalCode ?? '—',
              })}
            </p>
          )}
          <p className="mt-1 text-[11px] text-on-surface-variant">{t('imports.rosenkrantzBeleg.nameWarnung')}</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-on-surface-variant border-b border-outline-variant">
              <th className="py-1.5 pr-2">#</th>
              <th className="py-1.5 pr-2">{t('imports.rosenkrantzBeleg.artikel')}</th>
              <th className="py-1.5 pr-2">{t('imports.rosenkrantzBeleg.bezeichnung')}</th>
              <th className="py-1.5 pr-2 text-right">{t('imports.rosenkrantzBeleg.menge')}</th>
              <th className="py-1.5 pr-2">{t('imports.rosenkrantzBeleg.einheit')}</th>
              <th className="py-1.5 pr-2 text-right">{t('imports.rosenkrantzBeleg.einzelpreis')}</th>
              <th className="py-1.5 pr-2 text-right">{t('imports.rosenkrantzBeleg.netto')}</th>
              <th className="py-1.5 pr-2 text-right">{t('imports.rosenkrantzBeleg.mwst')}</th>
              <th className="py-1.5 text-right">{t('imports.rosenkrantzBeleg.brutto')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className="border-b border-outline-variant/40 last:border-0">
                <td className="py-1 pr-2 text-on-surface-variant">{l.LineNumber ?? i + 1}</td>
                <td className="py-1 pr-2 font-mono">{l.ItemNumber ?? '—'}</td>
                <td className="py-1 pr-2">{l.Description ?? '—'}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{num(l.Quantity)}</td>
                <td className="py-1 pr-2">{l.Unit ?? ''}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{eur(l.UnitPrice)}{l.DiscountPercent ? <span className="text-on-surface-variant"> −{num(l.DiscountPercent, 2)} %</span> : null}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{eur(l.NetAmount)}</td>
                <td className="py-1 pr-2 text-right tabular-nums text-on-surface-variant">{l.TaxPercent != null ? `${num(l.TaxPercent, 1)} %` : '—'}</td>
                <td className="py-1 text-right tabular-nums">{eur(l.GrossAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap justify-end gap-6 text-xs">
        {(p.TaxSummary ?? []).map((s, i) => (
          <div key={i} className="text-on-surface-variant">
            {t('imports.rosenkrantzBeleg.mwstSatz', { satz: num(s.TaxPercent, 1) })}: {eur(s.TaxableAmount)} → {eur(s.TaxAmount)}
          </div>
        ))}
        <div className="text-right">
          <div>{t('imports.rosenkrantzBeleg.netto')}: <span className="tabular-nums">{eur(p.Totals?.NetAmount)}</span></div>
          <div>{t('imports.rosenkrantzBeleg.mwst')}: <span className="tabular-nums">{eur(p.Totals?.TaxAmount)}</span></div>
          <div className="font-semibold">{t('imports.rosenkrantzBeleg.brutto')}: <span className="tabular-nums">{eur(p.Totals?.GrossAmount)}</span></div>
        </div>
      </div>
      </>
      )}
    </div>
  )
}
